import { addMinutes, format } from 'date-fns';
import { capitalizeFirstLetter } from './capitalize';
import {
    ColDef,
    ColDefField,
    IDateFilterParams,
    ISetFilterParams,
    SetFilterValuesFuncParams,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-enterprise';
import { t } from 'i18next';

export const datesBetween = (daysBefore: number, daysAfter: number) => {
    let dates: Date[] = [];
    let currentDate = new Date();
    const startDate = new Date(currentDate);
    startDate.setDate(startDate.getDate() - daysBefore);
    startDate.setHours(12, 0, 0, 0);

    const endDate = new Date(currentDate);
    endDate.setDate(endDate.getDate() + daysAfter);
    endDate.setHours(12, 0, 0, 0);

    while (startDate.getTime() <= endDate.getTime()) {
        dates.push(new Date(startDate));
        startDate.setDate(startDate.getDate() + 1);
    }
    return dates;
};

export const getTomorrowDate = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
};

export const getInOneDayDate = (date: Date) => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + 1);
    return newDate;
};

export const getOneDayAgoDate = (date: Date) => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() - 1);
    return newDate;
};

export const getSpecificDateFromDate = (date: Date, days: number) => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + days);
    return newDate;
};

export const isRollbackPossible = (date: string) => {
    const nowDate = new Date();
    const limitDate = new Date(date);
    limitDate.setHours(8, 0, 0, 0);
    return nowDate.getTime() < limitDate.getTime();
};

export const formatDate = (date: Date) => {
    let month = '' + (date.getMonth() + 1);
    let day = '' + date.getDate();
    let year = date.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
};

export const formatDateToYYYYMMDD = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export const formatDateToDDMMYYYY = (date: Date, separator = '-') => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${day}${separator}${month}${separator}${year}`;
};

export const getTodayPeriod = (date: Date = new Date()): 'normal' | 'halloween' | 'christmas' | 'newYear' => {
    const month = date.getMonth() + 1;
    const day = date.getDate();

    if ((month === 10 && day >= 15) || (month === 11 && day === 1)) {
        return 'halloween';
    }

    if (month === 12) {
        return 'christmas';
    }

    if (month === 1 && day <= 10) {
        return 'newYear';
    }

    return 'normal';
};

export const isSameDayOrBefore = (date1: Date, date2: Date): boolean => {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

    return d1.getTime() <= d2.getTime();
};

export const isSameDayOrAfter = (date1: Date, date2: Date): boolean => {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

    return d1.getTime() >= d2.getTime();
};

export const getDaysDifference = (date1: Date, date2: Date): number => {
    const timeDifference = date2.getTime() - date1.getTime();
    return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
};

export const sameDay = (d1: Date, d2: Date) => {
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
};

export const removeDuplicateDates = <T>(data: T[], getDate: (item: T) => string): T[] => {
    const seenDates = new Set<string>();
    return data.filter((item) => {
        const date = getDate(item);
        if (seenDates.has(date)) {
            return false;
        }
        seenDates.add(date);
        return true;
    });
};

export const formatTime = (time: Date | undefined) => {
    if (time === undefined) return '';
    const hours = time.getHours().toString().padStart(2, '0');
    const minutes = time.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

export const getDateXDaysBefore = (nbDays: number) => {
    let date = new Date();
    date.setDate(date.getDate() - nbDays);
    return date;
};

export const getDateXDaysAfter = (nbDays: number) => {
    let date = new Date();
    date.setDate(date.getDate() + nbDays);
    return date;
};

export const decimalToHM = (decimal: number): string => {
    const hours = parseInt(decimal.toString());
    const minutes = Math.round((decimal - hours) * 60);
    return `${hours.toString().padStart(2, '0')}h${minutes.toString().padStart(2, '0')}`;
};

export const hmToDecimal = (input: string): number => {
    const clean = input.trim().replace(/h|H/g, ':');
    const [h, m] = clean.split(':').map(Number);
    const hours = isNaN(h) ? 0 : h;
    const minutes = isNaN(m) ? 0 : m;
    return parseFloat((hours + minutes / 60).toFixed(2));
};

export const createDateColumn = <T = any>(field: ColDefField<T, any>, headerKey: string): ColDef<T> => ({
    headerName: capitalizeFirstLetter(t(headerKey)),
    field: field,
    cellDataType: 'date',
    filter: 'agDateColumnFilter',
    valueGetter: (params: ValueGetterParams<T>): Date | null => {
        if (!params.data) return null;
        const fieldValue = (params.data as any)[field as string] as string | null | undefined;
        if (!fieldValue) return null;
        const isoString = fieldValue.replace(' ', 'T');
        const date = new Date(isoString);
        return isNaN(date.getTime()) ? null : date;
    },
    valueFormatter: (params: ValueFormatterParams<T>): string => {
        if (!params.value) return '';
        try {
            return new Date(params.value).toLocaleDateString(t('locale'));
        } catch {
            return '';
        }
    },
    filterParams: {
        comparator: (filterDate: Date, cellValue: Date): number => {
            if (!cellValue) return -1;

            const filterDateOnly = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
            const cellDateOnly = new Date(cellValue.getFullYear(), cellValue.getMonth(), cellValue.getDate());

            if (cellDateOnly < filterDateOnly) return -1;
            if (cellDateOnly > filterDateOnly) return 1;
            return 0;
        },
        browserDatePicker: true,
        minValidYear: 2000,
    },
    colId: headerKey,
});

export const createTimeColumn = <T = any>(field: ColDefField<T, any>, headerKey: string): ColDef<T> => ({
    headerName: capitalizeFirstLetter(t(headerKey)),
    field: field,
    valueGetter: (params: ValueGetterParams<T>): string | null => {
        if (!params.data) return null;
        const fieldValue = (params.data as any)[field as string] as string | null | undefined;
        if (!fieldValue) return null;

        const date = new Date(fieldValue);
        return date.toLocaleTimeString(t('locale'), {
            hour: '2-digit',
            minute: '2-digit',
        });
    },
    filter: 'agSetColumnFilter',
    filterParams: {
        values: (params: SetFilterValuesFuncParams) => {
            const values = new Set<string>();
            params.api.forEachNodeAfterFilter((node) => {
                if (node.data) {
                    const fieldValue = (node.data as any)[field as string];
                    if (fieldValue) {
                        const date = new Date(fieldValue);
                        values.add(
                            date.toLocaleTimeString(t('locale'), {
                                hour: '2-digit',
                                minute: '2-digit',
                            })
                        );
                    }
                }
            });
            const sortedValues = Array.from(values).sort();
            params.success(sortedValues);
        },
    } as ISetFilterParams,
    valueFormatter: (params: ValueFormatterParams<T>): string => params.value || '',
    colId: headerKey,
});

export const createDateTimeColumn = <T = any>(field: ColDefField<T, any>, headerKey: string): ColDef<T> => ({
    headerName: capitalizeFirstLetter(t(headerKey)),
    field: field,
    cellDataType: 'dateTime',
    filter: 'agDateColumnFilter',
    valueGetter: (params: ValueGetterParams<T>): Date | null => {
        if (!params.data) return null;
        const fieldValue = (params.data as any)[field as string] as string | null | undefined;
        if (!fieldValue) return null;

        const isoString = fieldValue.replace(' ', 'T');
        const date = new Date(isoString);
        return isNaN(date.getTime()) ? null : date;
    },
    valueFormatter: (params: ValueFormatterParams<T>): string => {
        if (!params.value) return '';
        try {
            return new Date(params.value).toLocaleString(t('locale'));
        } catch {
            return '';
        }
    },
    colId: headerKey,
});
